import { FormNotice } from '@zappyrent/hundop';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { upperFirst, upperCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TelephoneInput from '../../../components/___design/TelephoneInput';
import TextInput from '../../../components/___design/TextInput';
import { H2Text, H3Text } from '../../../components/UI/Text';
import visitsAPI from '../../../services/api/visits';
import { emailValidationPattern } from '../../../shared/helpers';
import { VisitDto } from '../../../types/visits';
import {
  StyledButton,
  StyledContainer,
  StyledInputGroupContainer,
  StyledInputGroupRowContainer,
  StyledMainContainer,
} from './AddVisitInputs.style';

const ZappyrentSiteURL = process.env.REACT_APP_SITE_URL;

type IAddVisitFieldsState = {
  errorLabel: string;
  hasErrors: boolean;
  isEditing: boolean;
  isSubmitClicked: boolean;
  label?: string;
  name: keyof Omit<VisitDto, 'id'>;
  type: 'text' | 'email' | 'tel';
  value: string | null | undefined;
};

const validateField = (field: IAddVisitFieldsState): boolean => {
  if (field.type === 'email') {
    const x = !!field?.value?.match(emailValidationPattern);
    return x;
  } else if (field.type === 'tel') {
    return isValidPhoneNumber(field.value || '');
  } else if (field.type === 'text') {
    return (
      field.value !== null &&
      field.value !== undefined &&
      field.value.replace(/\s/g, '') !== ''
    );
  }
  return false;
};

const AddVisitInputs = ({
  property_id,
}: {
  property_id: string;
}): JSX.Element | null => {
  const { t, i18n } = useTranslation(['form', 'common']);
  console.log('i18n.language: ', i18n.language);
  const defaultFormErrorLabel = t('requiredField');
  const [fields, setFields] = useState<IAddVisitFieldsState[]>([
    {
      errorLabel: defaultFormErrorLabel,
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      label: upperFirst(t('fullname')),
      name: 'tenant_full_name',
      type: 'text',
      value: '',
    },
    {
      errorLabel: t('emailIsNotValid'),
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      label: t('form:email'),
      name: 'email',
      type: 'email',
      value: '',
    },
    {
      errorLabel: t('form:phoneIsNotValid'),
      hasErrors: false,
      isEditing: false,
      isSubmitClicked: false,
      label: t('form:phoneNumber'),
      name: 'phone_no',
      type: 'tel',
      value: '',
    },
  ]);

  const handleInputChange = useCallback(
    (name: string, value: string) =>
      setFields(prevFields =>
        prevFields.map(item => {
          if (
            item.name === name &&
            (item.type === 'text' ||
              item.type === 'tel' ||
              item.type === 'email')
          ) {
            const hasErrors = !validateField({ ...item, value });

            return {
              ...item,
              value,
              hasErrors,
            };
          }

          return item;
        }),
      ),
    [setFields],
  );
  const isDisabled = useMemo(
    () => fields.some(field => field.hasErrors),
    [fields],
  );
  const submitForm = useCallback(
    async (addVisitFieldsState: IAddVisitFieldsState[]) => {
      const body = addVisitFieldsState.reduce((prev, currItem) => {
        return {
          ...prev,
          [currItem.name]: currItem.value,
        };
      }, {} as VisitDto);
      const visit = await visitsAPI.createVisit(property_id, body);
      window.location.replace(
        `${ZappyrentSiteURL}/${i18n.language}/visits/${visit.id}/questionnaire`,
      );
    },
    [property_id],
  );
  const handleContinueClick = useCallback(
    () =>
      setFields(prevFields => {
        let IsNotValid = false;
        const newFields = prevFields.map(item => {
          if (!validateField(item)) {
            IsNotValid = true;
            return {
              ...item,
              hasErrors: true,
            };
          }
          return item;
        });
        if (!IsNotValid) {
          submitForm(newFields);
        }
        return newFields;
      }),
    fields,
  );

  return (
    <StyledMainContainer>
      <StyledContainer padding="4em 2.5rem">
        <H2Text textAlign="start">
          {t('form:CompleteThisFormAndWeWillContactYouToScheduleYourVisit')}
        </H2Text>
        <H3Text textAlign="start" fontWeight="normal">
          {t('form:ProvideYourContactInformation')}
        </H3Text>
        <StyledInputGroupContainer>
          <H3Text textAlign="start">{t('form:ContactInformation')}</H3Text>
          <StyledInputGroupRowContainer>
            {fields[0].type === 'text' && (
              <TextInput
                id={fields[0].name}
                label={fields[0].label}
                name={fields[0].name}
                type={fields[0].type}
                value={fields[0].value ?? ''}
                onChange={event =>
                  handleInputChange(fields[0].name, event.target.value)
                }
                user="tenant"
              >
                {fields[0].hasErrors && (
                  <FormNotice>{fields[0].errorLabel}</FormNotice>
                )}
              </TextInput>
            )}
          </StyledInputGroupRowContainer>
          <StyledInputGroupRowContainer>
            {fields[1].type === 'email' && (
              <TextInput
                id={fields[1].name}
                label={fields[1].label}
                name={fields[1].name}
                type={fields[1].type}
                value={fields[1].value ?? ''}
                onChange={event =>
                  handleInputChange(fields[1].name, event.target.value)
                }
                user="tenant"
              >
                {fields[1].hasErrors && (
                  <FormNotice>{fields[1].errorLabel}</FormNotice>
                )}
              </TextInput>
            )}
            {fields[2].type === 'tel' && (
              <TelephoneInput
                id={fields[2].name}
                label={fields[2].label ?? ''}
                name={fields[2].name}
                value={fields[2].value ?? ''}
                onChange={handleInputChange.bind(undefined, fields[2].name)}
              >
                {fields[2].hasErrors && (
                  <FormNotice>{fields[2].errorLabel}</FormNotice>
                )}
              </TelephoneInput>
            )}
          </StyledInputGroupRowContainer>
        </StyledInputGroupContainer>
      </StyledContainer>
      <StyledContainer justifyContent="end" padding="0 1rem">
        <StyledButton onClick={handleContinueClick} disabled={isDisabled}>
          {upperCase(t('common:continue'))}
        </StyledButton>
      </StyledContainer>
    </StyledMainContainer>
  );
};
export default AddVisitInputs;
